/* KaartZeker pages layer: month tabs, buy box (ticket rows / seatmap selection, stepper,
   live total, Direct afrekenen), read-more, recommendations, countdown.
   No dependencies. Safe to load more than once and to re-run after theme-editor reloads. */
(() => {
  if (window.__kzUi) return;
  window.__kzUi = true;

  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  };

  const formatMoney = (cents, fmt) => {
    const n = (cents / 100).toFixed(2);
    const [int, dec] = n.split('.');
    const dots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const commas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const map = {
      amount: `${commas}.${dec}`,
      amount_no_decimals: commas,
      amount_with_comma_separator: `${dots},${dec}`,
      amount_no_decimals_with_comma_separator: dots,
      amount_with_space_separator: `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`,
    };
    const tpl = fmt || '€{{amount_with_comma_separator}}';
    const out = tpl.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => map[k] ?? map.amount_with_comma_separator);
    const tmp = document.createElement('span');
    tmp.innerHTML = out;
    return tmp.textContent || out;
  };

  /* ---------- Month tabs + venue filter (kz-collection) ---------- */
  const initTabs = (root) => {
    if (!once(root, 'kzInit')) return;
    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const scope = root.closest('.kz-section') || document;
    const groups = Array.from(scope.querySelectorAll('[data-kz-group]'));
    const venueSelect = root.querySelector('[data-kz-venue-filter]');
    const emptyMsg = scope.querySelector('[data-kz-filter-empty]');
    const allKey = tabs.length ? tabs[0].dataset.kzTab : 'alle'; // first tab shows everything
    const state = { tab: allKey, venue: '' };

    const apply = () => {
      tabs.forEach((t) => {
        const on = t.dataset.kzTab === state.tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      let visibleCards = 0;
      groups.forEach((g) => {
        const inTab = state.tab === allKey || g.dataset.kzGroup === state.tab;
        let shown = 0;
        g.querySelectorAll('[data-kz-venue]').forEach((card) => {
          const match = !state.venue || card.dataset.kzVenue === state.venue;
          card.hidden = !match;
          if (match) shown += 1;
        });
        g.hidden = !inTab || shown === 0;
        if (!g.hidden) visibleCards += shown;
      });
      if (emptyMsg) emptyMsg.hidden = visibleCards > 0;
    };
    const writeUrl = () => {
      const parts = [];
      if (state.tab !== allKey) parts.push(state.tab);
      if (state.venue) parts.push(`zaal=${state.venue}`);
      history.replaceState(null, '', `${location.pathname}${location.search}${parts.length ? `#${parts.join('&')}` : ''}`);
    };
    const readUrl = () => {
      const parts = decodeURIComponent(location.hash.replace('#', '')).split('&').filter(Boolean);
      state.tab = allKey;
      state.venue = '';
      parts.forEach((part) => {
        if (part.startsWith('zaal=')) {
          const v = part.slice(5);
          if (venueSelect && Array.from(venueSelect.options).some((o) => o.value === v)) state.venue = v;
        } else if (tabs.some((t) => t.dataset.kzTab === part)) state.tab = part;
      });
      if (venueSelect) venueSelect.value = state.venue;
      apply();
    };

    tabs.forEach((t, i) => {
      t.addEventListener('click', () => { state.tab = t.dataset.kzTab; apply(); writeUrl(); });
      t.addEventListener('keydown', (e) => {
        const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        const next = tabs[(i + dir + tabs.length) % tabs.length];
        next.focus();
        state.tab = next.dataset.kzTab;
        apply();
        writeUrl();
      });
    });
    venueSelect?.addEventListener('change', () => { state.venue = venueSelect.value; apply(); writeUrl(); });
    window.addEventListener('hashchange', readUrl);
    readUrl();
  };

  /* ---------- Buy box (kz-event-main) ----------
     One card per ticket variant, each with its own stepper, total and "Direct afrekenen".
     Seated variants are sold in packs (data-pack): the stepper starts at the pack and moves in packs.
     The shared Horizon form (express button + "In winkelwagen") follows the active card.
     Seated events: cards stay hidden until the seat map reports a block
     (`kz:vak-select` { vak, ring, label, variantIds } / `kz:vak-clear` on <kz-seatmap>). */
  const initBuy = (root) => {
    if (!once(root, 'kzInit')) return;
    const seated = root.dataset.kzBuy === 'seated';
    const fmt = root.dataset.moneyFormat;
    const cards = Array.from(root.querySelectorAll('[data-kz-ticket]'));
    const idInput = root.querySelector('form input[name="id"]');
    const qtyInput = root.querySelector('form input[name="quantity"]');
    const packInput = root.querySelector('[data-kz-pack-input]');
    const errEl = root.querySelector('[data-kz-error]');
    const hint = root.querySelector('[data-kz-hint]');
    const body = root.querySelector('[data-kz-body]');
    const detailTitle = root.querySelector('[data-kz-detail-title]');
    const sticky = root.querySelector('[data-kz-sticky]');
    const stickyPrice = root.querySelector('[data-kz-sticky-price]');
    if (!idInput || !cards.length) return;

    const variants = {};
    const qty = {};
    cards.forEach((el) => {
      const id = el.dataset.variantId;
      const pack = Math.max(1, Number(el.dataset.pack) || 1);
      const max = Number(el.dataset.max) || 0;
      variants[id] = { el, price: Number(el.dataset.price) || 0, max, pack, vak: el.dataset.vak || '', ring: el.dataset.ring || '', available: el.dataset.available === 'true' };
      qty[id] = Math.min(pack, max);
    });
    const usable = (id) => !!variants[id] && variants[id].available && variants[id].max > 0;
    const state = { id: '' };
    const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const addBtn = () => root.querySelector('button[name="add"]');

    const render = () => {
      cards.forEach((el) => {
        const id = el.dataset.variantId;
        const v = variants[id];
        const active = id === state.id;
        el.classList.toggle('is-active', active);
        const radio = el.querySelector('input[type="radio"]');
        if (radio) radio.checked = active;
        const out = el.querySelector('output');
        if (out) out.value = String(qty[id]);
        const minus = el.querySelector('[data-kz-minus]');
        const plus = el.querySelector('[data-kz-plus]');
        if (minus) minus.disabled = qty[id] - v.pack < v.pack;
        if (plus) plus.disabled = qty[id] + v.pack > v.max;
        const total = el.querySelector('[data-kz-row-total]');
        if (total && usable(id)) {
          const sub = qty[id] > 1 ? ` <small>(${qty[id]} × ${esc(formatMoney(v.price, fmt))})</small>` : '';
          total.innerHTML = `${esc(root.dataset.strTotal || '')} <b>${esc(formatMoney(v.price * qty[id], fmt))}</b>${sub}`;
        }
      });
      const v = variants[state.id];
      const ok = usable(state.id);
      if (ok) {
        idInput.value = state.id;
        if (qtyInput) qtyInput.value = String(qty[state.id]);
        if (packInput) { packInput.disabled = v.pack < 2; packInput.value = v.pack < 2 ? '' : String(v.pack); }
      }
      if (stickyPrice) {
        stickyPrice.innerHTML = ok
          ? `${esc(formatMoney(v.price * qty[state.id], fmt))}<small>${qty[state.id]} × ${esc(formatMoney(v.price, fmt))}</small>`
          : esc(root.dataset.strChoose || '');
      }
      const atc = addBtn();
      if (atc) atc.disabled = !ok;
      root.querySelector('.accelerated-checkout-block')?.toggleAttribute('hidden', !ok);
    };

    const setActive = (id) => {
      if (!usable(String(id))) return false;
      state.id = String(id);
      render();
      return true;
    };

    // Direct afrekenen SETS the cart quantity of the chosen variant (never adds on top of what is
    // already there), then continues to the cart (transfer/app events) or straight to checkout.
    const cartUrl = (path) => `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}${path}`;
    const readCart = async () => (await fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })).json();
    const fail = (btn, msg) => {
      if (errEl) { errEl.innerHTML = msg; errEl.classList.add('is-visible'); }
      if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
    };
    const buy = async (btn) => {
      if (!usable(state.id)) return;
      const v = variants[state.id];
      const props = {};
      if (v.pack > 1) props._kz_pack = String(v.pack);
      btn?.setAttribute('aria-busy', 'true');
      if (btn) btn.disabled = true;
      errEl?.classList.remove('is-visible');
      // The button turns into the green check right away (a ring spins while the cart updates);
      // the checkout opens as soon as both are done. On an error the button comes back as it was
      // and the message shows.
      let goTo, stop;
      const job = new Promise((res, rej) => { goTo = res; stop = rej; });
      job.catch(() => {});
      const checking = kzCheck(btn, job);
      try {
        const cart = await readCart();
        const line = (cart.items || []).find((it) => String(it.variant_id) === state.id);
        let res;
        if (line) {
          res = await fetch(cartUrl('cart/change.js'), {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: qty[state.id], properties: Object.assign({}, line.properties || {}, props) }),
          });
        } else {
          const item = { id: Number(state.id), quantity: qty[state.id] };
          if (Object.keys(props).length) item.properties = props;
          res = await fetch(root.dataset.cartAddUrl || cartUrl('cart/add.js'), {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] }),
          });
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || '');
        const to = root.dataset.checkoutUrl || cartUrl('checkout');
        if (checking) goTo(() => { location.href = to; });
        else location.href = to;
      } catch (err) {
        stop(err);
        fail(btn, [root.dataset.strError || '', err.message].filter(Boolean).join(' '));
      }
    };

    // "In winkelwagen" (Horizon's form) must never push a line above the available stock.
    let cartQty = {};
    const loadCartQty = () => readCart().then((c) => { cartQty = {}; (c.items || []).forEach((it) => { cartQty[String(it.variant_id)] = (cartQty[String(it.variant_id)] || 0) + it.quantity; }); }).catch(() => {});
    loadCartQty();
    document.addEventListener('cart:update', loadCartQty);
    root.querySelector('form')?.addEventListener('submit', (e) => {
      const v = variants[state.id];
      if (!v) return;
      const inCart = cartQty[state.id] || 0;
      const room = v.max - inCart;
      if (room <= 0) {
        e.preventDefault(); e.stopImmediatePropagation();
        fail(null, `${root.dataset.strAllInCart || ''} <a href="${cartUrl('cart')}">${root.dataset.strToCart || ''}</a>`);
        return;
      }
      if (qty[state.id] > room) {
        const capped = v.pack > 1 ? Math.floor(room / v.pack) * v.pack : room;
        if (capped <= 0) { e.preventDefault(); e.stopImmediatePropagation(); fail(null, `${root.dataset.strAllInCart || ''} <a href="${cartUrl('cart')}">${root.dataset.strToCart || ''}</a>`); return; }
        qty[state.id] = capped;
        render();
      }
      setTimeout(loadCartQty, 2500);
    }, true);

    cards.forEach((el) => {
      const id = el.dataset.variantId;
      if (!usable(id)) return;
      const v = variants[id];
      el.addEventListener('click', (e) => { if (!e.target.closest('button')) setActive(id); });
      el.querySelector('input[type="radio"]')?.addEventListener('change', () => setActive(id));
      el.querySelector('[data-kz-plus]')?.addEventListener('click', () => {
        if (qty[id] + v.pack <= v.max) qty[id] += v.pack;
        setActive(id);
      });
      el.querySelector('[data-kz-minus]')?.addEventListener('click', () => {
        if (qty[id] - v.pack >= v.pack) qty[id] -= v.pack;
        setActive(id);
      });
      el.querySelector('[data-kz-card-buy]')?.addEventListener('click', (e) => { if (setActive(id)) buy(e.currentTarget); });
    });

    if (seated) {
      const home = document.createComment('kz-buy-home');
      root.before(home);
      const map = document.querySelector('kz-seatmap');
      let lastEvent = 0;
      let autoSelecting = false;
      const showVak = (ids, label) => {
        const set = new Set((ids || []).map(String));
        let first = '';
        cards.forEach((el) => {
          const show = set.has(el.dataset.variantId);
          el.hidden = !show;
          if (show && !first && usable(el.dataset.variantId)) first = el.dataset.variantId;
        });
        if (!set.size) return clearVak();
        // cheapest listing first
        const list = cards[0].parentElement;
        cards.filter((el) => !el.hidden).sort((x, y) => Number(x.dataset.price) - Number(y.dataset.price)).forEach((el) => list.append(el));
        if (hint) hint.hidden = true;
        if (body) body.hidden = false;
        const slot = document.querySelector('[data-kz-panel-detail]');
        if (slot) {
          if (root.parentElement !== slot) slot.append(root);
          root.classList.add('is-in-panel');
          home.parentElement?.classList.add('is-vacant');
          if (detailTitle) detailTitle.hidden = true;
        } else if (detailTitle) {
          detailTitle.textContent = label || '';
          detailTitle.hidden = !label;
        }
        state.id = '';
        if (first) setActive(first); else render();
        if (!autoSelecting && window.matchMedia('(max-width: 989px)').matches) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
      };
      const clearVak = () => {
        cards.forEach((el) => { el.hidden = true; });
        state.id = '';
        if (body) body.hidden = true;
        if (hint) hint.hidden = false;
        if (detailTitle) detailTitle.hidden = true;
        if (root.classList.contains('is-in-panel')) {
          home.after(root);
          root.classList.remove('is-in-panel');
          home.parentElement?.classList.remove('is-vacant');
        }
        render();
      };
      const idsForVak = (vak) => cards.filter((el) => (el.dataset.vak || '') === vak).map((el) => el.dataset.variantId);
      const labelFor = (id) => {
        const v = variants[id];
        if (!v) return '';
        const name = /^\d+$/.test(v.vak) ? (root.dataset.strSection || 'Vak [section]').replace('[section]', v.vak) : (v.el.dataset.title || '').split(' · ')[0];
        return [name, v.ring].filter(Boolean).join(' · ');
      };
      document.addEventListener('kz:vak-select', (e) => {
        lastEvent = Date.now();
        const d = e.detail || {};
        const ids = (d.variantIds && d.variantIds.length) ? d.variantIds : idsForVak(String(d.vak || '').toUpperCase());
        showVak(ids, [d.label, d.ring].filter(Boolean).join(' · '));
      });
      document.addEventListener('kz:vak-clear', () => { lastEvent = Date.now(); clearVak(); });

      // Fallback for a seat map that does not dispatch the events (yet): its "Bekijk" writes
      // the variant id into the form / the URL.
      let lastInput = idInput.value;
      const sync = () => {
        if (Date.now() - lastEvent < 400) { lastInput = idInput.value; return; }
        const fromUrl = new URL(location.href).searchParams.get('variant');
        const cand = idInput.value !== lastInput ? idInput.value : fromUrl;
        lastInput = idInput.value;
        if (cand && usable(cand) && cand !== state.id) {
          showVak(idsForVak(variants[cand].vak), labelFor(cand));
          setActive(cand);
        }
      };
      (map || document).addEventListener('click', (e) => {
        if (e.target.closest('[data-kz-select]')) setTimeout(sync, 30);
      });
      const fromUrl = new URL(location.href).searchParams.get('variant');
      const blocks = [...new Set(Object.keys(variants).filter(usable).map((id) => variants[id].vak))];
      if (fromUrl && usable(fromUrl)) { showVak(idsForVak(variants[fromUrl].vak), labelFor(fromUrl)); setActive(fromUrl); }
      else if (blocks.length === 1) {
        // Exactly one block on offer: open it straight away, through the seat map's own click
        // handling so the map highlights too; fall back to showing the cards ourselves.
        render();
        const open = () => {
          autoSelecting = true;
          const seg = map?.querySelector('.kz-seg--open');
          if (seg) seg.dispatchEvent(new MouseEvent('click', { bubbles: true }));
          setTimeout(() => {
            if (body && body.hidden) showVak(idsForVak(blocks[0]), labelFor(Object.keys(variants).find(usable)));
            autoSelecting = false;
          }, 60);
        };
        if (map && window.customElements) customElements.whenDefined('kz-seatmap').then(open); else open();
      } else render();
    } else {
      const list = cards[0].parentElement;
      [...cards].sort((x, y) => Number(x.dataset.price) - Number(y.dataset.price)).forEach((el) => list.append(el));
      const pre = idInput.dataset.kzPreselect;
      const cheapest = Object.keys(variants).filter(usable).sort((x, y) => variants[x].price - variants[y].price)[0];
      if (!setActive(usable(pre) && new URL(location.href).searchParams.get('variant') ? pre : cheapest || '')) render();
    }

    // Mobile sticky bar: visible while the buy box is off-screen.
    if (sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { sticky.hidden = entry.isIntersecting; }, { threshold: 0 }).observe(root);
      sticky.querySelector('[data-kz-sticky-buy]')?.addEventListener('click', (e) => {
        if (usable(state.id)) buy(e.currentTarget);
        else (document.getElementById('kies-je-plek') || root).scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  /* ---------- Cart: pack sizes + "account details after payment" notice ----------
     Which products are delivered to an account (Ticketmaster/app) comes from <script id="kz-cart-meta">
     (every cart line + the current product) and a sessionStorage cache filled on product pages. */
  const initCartPacks = () => {
    if (window.__kzCartPacks) return;
    window.__kzCartPacks = true;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let packs = {};
    let items = [];
    const meta = (() => { try { return JSON.parse(document.getElementById('kz-cart-meta')?.textContent || '{}'); } catch { return {}; } })();
    const kinds = Object.assign({}, meta.products || {});
    try { Object.assign(kinds, JSON.parse(sessionStorage.getItem('kzKinds') || '{}')); sessionStorage.setItem('kzKinds', JSON.stringify(kinds)); } catch { /* ignore */ }
    const str = meta.strings || {};
    const kindOf = (it) => kinds[String(it.product_id)] || '';
    // No account form in the cart any more: the Ticketmaster/app details are asked AFTER payment
    // (order e-mail + /pages/ticketgegevens). The cart only says so, right above the checkout button.
    const gate = () => {
      const need = items.some((it) => kindOf(it));
      document.querySelectorAll('button[name="checkout"], .cart__checkout-button').forEach((btn) => {
        if (btn.closest('.kz-cart__bar')) return;
        const host = btn.parentElement;
        let note = host?.querySelector(':scope > .kz-cart-after');
        if (need && host && str.after) {
          if (!note) { note = document.createElement('p'); note.className = 'kz-cart-after'; note.innerHTML = str.after; btn.before(note); }
        } else note?.remove();
      });
    };

    const lineMeta = () => {
      document.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
        const it = items.find((x) => x.key === row.dataset.key);
        if (!it) return;
        const title = row.querySelector('.cart-items__title-row') || row.querySelector('.cart-items__title');
        if (!title || row.querySelector('.kz-cartline__meta')) return;
        const link = row.querySelector('.cart-items__title');
        if (link && !link.dataset.kzTrim) { link.dataset.kzTrim = '1'; link.textContent = link.textContent.trim().replace(/\s[–-]\sTickets$/, ''); }
        const when = (meta.lines || {})[String(it.product_id)] || '';
        const pack = Number(it.properties && it.properties._kz_pack) || 0;
        const parts = [];
        if (when) parts.push(`<span class="kz-cartline__when">${when.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))}</span>`);
        if (pack > 1 && str.packNote) parts.push(`<span class="kz-cartline__pack">${str.packNote.replace('[n]', String(pack))}</span>`);
        if (!parts.length) return;
        const el = document.createElement('div');
        el.className = 'kz-cartline__meta';
        el.innerHTML = parts.join('');
        title.after(el);
      });
    };

    const applyDom = () => {
      document.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
        const pack = packs[row.dataset.key];
        if (!pack) return;
        const input = row.querySelector('input[ref="quantityInput"], input[name="updates[]"]');
        if (!input) return;
        input.min = String(pack);
        input.step = String(pack);
        const minus = row.querySelector('button[name="minus"]');
        if (minus) minus.disabled = Number(input.value) <= pack;
      });
    };
    let noteOn = false;
    try { noteOn = sessionStorage.getItem('kzPackNote') === '1'; sessionStorage.removeItem('kzPackNote'); } catch { /* ignore */ }
    const showNote = () => {
      if (!noteOn) return;
      const text = document.querySelector('meta[name="kz-cart-pack-note"]')?.content;
      if (!text) return;
      document.querySelectorAll('cart-items-component').forEach((host) => {
        if (host.previousElementSibling?.classList.contains('kz-cartnote')) return;
        const p = document.createElement('p');
        p.className = 'kz-cartnote';
        p.setAttribute('role', 'status');
        p.textContent = text;
        host.before(p);
      });
    };
    let busy = false;
    const refresh = async () => {
      if (busy) return;
      busy = true;
      try {
        const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
        items = cart.items || [];
        packs = {};
        const fixes = [];
        (cart.items || []).forEach((it) => {
          const pack = Number(it.properties && it.properties._kz_pack) || 0;
          if (pack < 2) return;
          packs[it.key] = pack;
          if (it.quantity % pack) fixes.push({ id: it.key, quantity: Math.floor(it.quantity / pack) * pack });
        });
        if (fixes.length) {
          for (const f of fixes) {
            await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(f) });
          }
          try { sessionStorage.setItem('kzPackNote', '1'); } catch { /* ignore */ }
          location.reload();
          return;
        }
        applyDom();
        lineMeta();
        gate();
        showNote();
      } catch { /* leave the cart as it is */ } finally { busy = false; }
    };
    let timer;
    new MutationObserver((muts) => {
      if (!muts.some((m) => m.target.closest?.('cart-items-component, cart-drawer-component, .cart-drawer') || Array.from(m.addedNodes).some((n) => n.nodeType === 1 && (n.matches?.('cart-items-component') || n.querySelector?.('cart-items-component'))))) return;
      clearTimeout(timer);
      timer = setTimeout(refresh, 250);
    }).observe(document.body, { childList: true, subtree: true });
    refresh();
  };

  /* ---------- Carousel (scroll-snap track + arrow buttons) ---------- */
  const initCarousel = (root) => {
    if (!once(root, 'kzInit')) return;
    const track = root.querySelector('[data-kz-track]');
    const prev = root.querySelector('[data-kz-prev]');
    const next = root.querySelector('[data-kz-next]');
    if (!track || !prev || !next) return;
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const scrollable = max > 4;
      prev.hidden = !scrollable || track.scrollLeft <= 4;
      next.hidden = !scrollable || track.scrollLeft >= max - 4;
    };
    const step = (dir) => {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: dir * Math.max(260, track.clientWidth * 0.8), behavior: reduce ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    track.addEventListener('scroll', update, { passive: true });
    // ResizeObserver runs after layout, so reading scrollWidth here never forces an extra layout
    // during page load (it used to cost ~80 ms on event pages).
    if (window.ResizeObserver) new ResizeObserver(update).observe(track);
    else { window.addEventListener('resize', update); requestAnimationFrame(update); }
  };

  /* ---------- Read more ---------- */
  const initClamp = (el) => {
    if (!once(el, 'kzInit')) return;
    const body = el.querySelector('.kz-desc__body');
    const btn = el.querySelector('.kz-desc__more');
    if (!body || !btn) return;
    if (body.scrollHeight <= body.clientHeight + 24) { el.removeAttribute('data-kz-clamp'); return; }
    btn.addEventListener('click', () => {
      const open = el.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? (btn.dataset.less || '') : (btn.dataset.more || '');
    });
  };

  /* ---------- Recommendations (Section Rendering API) ---------- */
  const initReco = async (el) => {
    if (!once(el, 'kzInit')) return;
    const { url } = el.dataset;
    if (!url) return;
    try {
      const html = await (await fetch(url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('[data-kz-reco-inner]');
      if (inner && inner.children.length) { el.innerHTML = inner.innerHTML; el.hidden = false; el.querySelectorAll('[data-kz-carousel]').forEach(initCarousel); } else el.remove();
    } catch { el.remove(); }
  };

  /* ---------- Countdown (keeps "over N dagen" right on cached pages) ---------- */
  const initCountdown = (el) => {
    const start = new Date(el.dataset.kzCountdown);
    if (Number.isNaN(start.getTime())) return;
    const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diff = Math.round((day(start) - day(new Date())) / 86400000);
    if (diff < 0) el.hidden = true;
    else if (el.dataset.strDays) el.textContent = diff === 0 ? el.dataset.strToday : diff === 1 ? el.dataset.strTomorrow : el.dataset.strDays.replace('[n]', String(diff));
  };

  /* Seated event, desktop: dock our buy box directly under the seat map's "Beschikbare vakken"
     panel (same right-hand column). The seat map markup belongs to another snippet, so this is
     done at runtime and falls back to the stacked layout when the panel is not found. */
  const initDock = (ev) => {
    const wrap = ev.querySelector('.kz-ev__panelwrap');
    const panel = ev.querySelector('.kz-ev__map [data-kz-panel]');
    if (!wrap || !panel || !panel.parentElement) return;
    const mq = window.matchMedia('(min-width: 990px)');
    const home = document.createComment('kz-panelwrap-home');
    wrap.before(home);
    let dock = null;
    const apply = () => {
      if (mq.matches && !dock) {
        dock = document.createElement('div');
        dock.className = 'kz-ev__dock';
        panel.before(dock);
        dock.append(panel, wrap);
        ev.classList.add('is-docked');
      } else if (!mq.matches && dock) {
        dock.before(panel);
        home.after(wrap);
        dock.remove();
        dock = null;
        ev.classList.remove('is-docked');
      }
    };
    mq.addEventListener('change', apply);
    apply();
  };

  /* Delivery line: pages are cached briefly, so refine the "< 24 h before start" branch client-side. */
  const initDelivery = (el) => {
    const start = Number(el.dataset.start) * 1000;
    if (!start) return;
    const rush = start - Date.now() < 86400000;
    const html = rush ? el.dataset.rush : el.dataset.normal;
    const span = el.querySelector('span');
    if (span && html && span.innerHTML !== html) span.innerHTML = html;
  };

  /* Hero search: suggestions from the 2nd letter, rendered by the kz-suggest section
     (same markup as the header search modal). Esc / click outside closes; arrows move. */
  const initSuggest = (form) => {
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    const pop = document.createElement('div');
    pop.className = 'kz-suggest-pop';
    pop.hidden = true;
    form.append(pop);
    form.classList.add('kz-suggest-host');
    input.setAttribute('aria-autocomplete', 'list');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let timer = 0;
    let ctrl = null;
    const close = () => { pop.hidden = true; input.setAttribute('aria-expanded', 'false'); };
    const fetchSuggest = async (q) => {
      ctrl?.abort();
      ctrl = new AbortController();
      const url = `${root}search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,collection,article&resources[limit]=10&resources[limit_scope]=each&section_id=kz-suggest`;
      try {
        const html = await (await fetch(url, { signal: ctrl.signal })).text();
        const box = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-kz-suggest]');
        if (input.value.trim() !== q) return;
        pop.innerHTML = box ? box.outerHTML : '';
        pop.hidden = !box;
        input.setAttribute('aria-expanded', String(!!box));
      } catch (e) { /* aborted or offline: keep the plain search form */ }
    };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { close(); return; }
      timer = setTimeout(() => fetchSuggest(q), 160);
    });
    input.addEventListener('focus', () => { if (pop.innerHTML && input.value.trim().length >= 2) pop.hidden = false; });
    const links = () => [...pop.querySelectorAll('a')];
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); input.focus(); return; }
      if (pop.hidden || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
      const all = links();
      if (!all.length) return;
      e.preventDefault();
      const i = all.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % all.length : i <= 0 ? -1 : i - 1;
      (next < 0 ? input : all[next]).focus();
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
  };

  /* Newsletter popup: opens at most once per browser (localStorage + cookie `kz_nl`), after the
     delay or at 40% scroll. After signing up the page reloads with ?customer_posted=true and the
     popup reopens once with "Check je inbox" (Klaviyo emails the code). */
  const initNlPop = (el) => {
    const KEY = 'kz_nl';
    const get = () => { try { return localStorage.getItem(KEY) || (document.cookie.match(/(?:^|; )kz_nl=([^;]*)/) || [])[1]; } catch (e) { return 'blocked'; } };
    const set = (v) => { try { localStorage.setItem(KEY, v); } catch (e) { /* private mode */ } document.cookie = `${KEY}=${v}; max-age=31536000; path=/; SameSite=Lax`; };
    const open = () => { el.hidden = false; document.documentElement.classList.add('kz-nlpop-open'); el.querySelector('input[type="email"], [data-kz-copy], [data-kz-nlpop-done] [data-kz-nlpop-close]')?.focus({ preventScroll: true }); };
    const close = () => { el.hidden = true; document.documentElement.classList.remove('kz-nlpop-open'); };
    el.addEventListener('click', (e) => { if (e.target === el || e.target.closest('[data-kz-nlpop-close]')) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !el.hidden) close(); });
    el.querySelector('form')?.addEventListener('submit', () => { try { sessionStorage.setItem('kz_nl_pending', '1'); } catch (e) { /* ignore */ } });
    el.querySelector('[data-kz-copy]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      try { await navigator.clipboard.writeText(btn.dataset.kzCopy); btn.querySelector('[data-kz-copy-label]').textContent = btn.dataset.kzCopied || 'Gekopieerd'; } catch (err) { /* manual copy */ }
    });
    let pending = false;
    try { pending = sessionStorage.getItem('kz_nl_pending') === '1'; } catch (e) { /* ignore */ }
    if (pending && el.querySelector('[data-kz-nlpop-done]')) {
      try { sessionStorage.removeItem('kz_nl_pending'); } catch (e) { /* ignore */ }
      set('subscribed');
      open();
      return;
    }
    if (el.dataset.preview === '1') { open(); return; }
    if (get()) return;
    let fired = false;
    const fire = () => {
      if (fired || get()) return;
      // never on top of an open dialog or while someone is typing / browsing search suggestions
      const banner = document.getElementById('shopify-pc__banner'); // Shopify cookie banner still open
      const bannerOpen = !!banner && banner.offsetParent !== null && getComputedStyle(banner).visibility !== 'hidden';
      const busy = bannerOpen || document.querySelector('dialog[open], .kz-suggest-pop:not([hidden])') || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || '');
      if (busy) { setTimeout(fire, 8000); return; }
      fired = true;
      set('shown');
      window.removeEventListener('scroll', onScroll);
      open();
    };
    let scrollFired = false;
    const onScroll = () => { if (!scrollFired && window.scrollY > (document.documentElement.scrollHeight - innerHeight) * 0.4) { scrollFired = true; fire(); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(fire, (Number(el.dataset.delay) || 12) * 1000);
  };

  /* ---------- Recent bekeken: event pages save, the search overlay shows (this browser only) ---------- */
  const RECENT_KEY = 'kz-recent';
  const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
  const writeRecent = (list) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* private mode: skip */ } };
  const saveRecent = () => {
    const el = document.querySelector('script[data-kz-recent-item]');
    if (!el || el.dataset.kzSaved) return;
    el.dataset.kzSaved = '1';
    try {
      const [title, url, img] = JSON.parse(`[${el.textContent.replace(/\|/g, ',')}]`);
      if (!title || !url) return;
      writeRecent([{ title, url, img }, ...readRecent().filter((r) => r.url !== url)].slice(0, 6));
    } catch { /* malformed: ignore */ }
  };
  const renderRecent = () => {
    document.querySelectorAll('[data-kz-recent]').forEach((box) => {
      const list = box.querySelector('[data-kz-recent-list]');
      const here = location.pathname;
      const items = readRecent().filter((r) => r.url !== here).slice(0, 5);
      box.hidden = items.length === 0;
      if (!list) return;
      list.replaceChildren(...items.map((r) => {
        const a = document.createElement('a');
        a.className = 'kz-recent__item';
        a.href = r.url;
        if (r.img) { const i = document.createElement('img'); i.src = r.img; i.alt = ''; i.loading = 'lazy'; a.append(i); }
        else { const d = document.createElement('span'); d.className = 'kz-recent__dot'; a.append(d); }
        const t = document.createElement('span'); t.textContent = r.title; a.append(t);
        return a;
      }));
      const clear = box.querySelector('[data-kz-recent-clear]');
      if (clear && !clear.dataset.kzOn) {
        clear.dataset.kzOn = '1';
        clear.addEventListener('click', (e) => { e.preventDefault(); writeRecent([]); renderRecent(); });
      }
    });
  };

  const boot = () => {
    saveRecent();
    renderRecent();
    document.querySelectorAll('[data-kz-nlpop]:not([data-kz-on])').forEach((el) => { el.dataset.kzOn = '1'; initNlPop(el); });
    document.querySelectorAll('.kz-lhero__search:not([data-kz-suggest-on])').forEach((f) => { f.dataset.kzSuggestOn = '1'; initSuggest(f); });
    document.querySelectorAll('[data-kz-delivery]').forEach(initDelivery);
    document.querySelectorAll('.kz-ev--seated:not([data-kz-dock])').forEach((ev) => { ev.dataset.kzDock = '1'; initDock(ev); });
    document.querySelectorAll('[data-kz-tabs]').forEach(initTabs);
    document.querySelectorAll('[data-kz-buy]').forEach(initBuy);
    initCartPacks();
    document.querySelectorAll('[data-kz-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-kz-clamp]').forEach(initClamp);
    document.querySelectorAll('[data-kz-reco]').forEach(initReco);
    document.querySelectorAll('[data-kz-countdown]').forEach(initCountdown);
  };
  // the search overlay can re-render its empty state: refresh "Recent bekeken" whenever it opens
  document.addEventListener('click', (e) => { if (e.target.closest('search-button, .predictive-search__reset-button')) setTimeout(renderRecent, 60); });
  // Card photo grows into the event page photo. Horizon's view-transitions.js does the page
  // transition; it only needs the clicked card's photo marked (same type as .kz-ev__media on the
  // event page). Set on click, not in the HTML, so a list restored from the back/forward cache
  // never has two marked photos.
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const card = e.target.closest('a.kz-ecard, a.kz-trow');
    if (!card || card.target === '_blank') return;
    const media = card.querySelector('.kz-ecard__media img, .kz-trow__media img')?.parentElement;
    if (!media) return;
    document.querySelectorAll('[data-view-transition-triggered]').forEach((el) => el.removeAttribute('data-view-transition-triggered'));
    media.setAttribute('data-view-transition-type', 'product-image-transition');
    media.setAttribute('data-view-transition-triggered', 'true');
  }, true);
  // "Direct afrekenen" and "Afrekenen" turn into the green KaartZeker check: the button keeps
  // its size (no layout shift) while clip-path shrinks what you see to a circle; a ring spins
  // while the cart updates, then the circle fills green and the check draws (CSS in kz-ui.css).
  // job: a promise for the function that leaves the page; a rejected job restores the button.
  // Returns false (caller continues at once) under reduced motion or for an invisible button.
  function kzCheckReset(btn) {
    btn.getAnimations().forEach((a) => a.cancel());
    btn.querySelectorAll('.kz-check').forEach((n) => n.remove());
    btn.classList.remove('kz-checking');
    btn.removeAttribute('aria-busy');
  }
  function kzCheck(btn, job) {
    if (!btn?.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    if (btn.classList.contains('kz-checking')) return false;
    const w = btn.offsetWidth, h = btn.offsetHeight;
    if (!w || !h) return false;
    const side = Math.max(0, (w - h) / 2);
    const from = getComputedStyle(btn).backgroundColor;
    const green = getComputedStyle(document.documentElement).getPropertyValue('--kz-success-bright').trim() || '#16a34a';
    const ov = document.createElement('span');
    ov.className = 'kz-check';
    ov.setAttribute('aria-hidden', 'true');
    ov.innerHTML = '<span class="kz-check__ring"></span><svg viewBox="0 0 24 24" width="26" height="26"><path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" pathLength="1"/></svg>';
    btn.classList.add('kz-checking');
    btn.setAttribute('aria-busy', 'true');
    btn.append(ov);
    const ease = 'cubic-bezier(.2, .8, .2, 1)';
    const shrink = btn.animate([{ clipPath: 'inset(0 0 round 999px)' }, { clipPath: `inset(0 ${side}px round 999px)` }], { duration: 320, easing: ease, fill: 'forwards' });
    const waiting = setTimeout(() => ov.classList.add('is-waiting'), 340);
    let left = false;
    const finish = async (leave) => {
      await shrink.finished.catch(() => {});
      clearTimeout(waiting);
      ov.classList.remove('is-waiting');
      btn.animate([{ backgroundColor: from }, { backgroundColor: green }], { duration: 220, easing: 'ease-out', fill: 'forwards' });
      ov.querySelector('path').animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 340, delay: 90, easing: 'ease-out', fill: 'forwards' });
      // leave while the check is drawing: the next page takes longer than the rest of the animation
      setTimeout(() => { if (!left) { left = true; leave(); } }, 260);
    };
    Promise.resolve(job).then(finish, () => kzCheckReset(btn));
    return true;
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('button[name="checkout"]');
    if (!btn || btn.disabled || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const form = btn.form;
    if (!form) return;
    if (kzCheck(btn, Promise.resolve(() => { try { form.requestSubmit(btn); } catch { form.submit(); } }))) e.preventDefault();
  });
  // Faster page changes. (1) Speculation rules: Chrome/Edge fetch a page's HTML when the pointer
  // rests on a link (or on touch start), so the next page is usually there before the click.
  // Prefetch only (no prerender), so analytics and pop-ups never run for pages nobody opens.
  // (2) Event cards: on hover/touch, warm the event page's large photo (same width the event page
  // picks), so the "photo grows" transition has the real image instead of an empty frame.
  if (!document.querySelector('script[data-kz-spec]') && window.HTMLScriptElement?.supports?.('speculationrules')) {
    const s = document.createElement('script');
    s.type = 'speculationrules';
    s.dataset.kzSpec = '1';
    s.textContent = JSON.stringify({ prefetch: [{ where: { and: [
      { href_matches: '/*' },
      { not: { href_matches: ['/cart*', '/checkout*', '/checkouts/*', '/account*', '/*/cart*', '/*/account*', '/apps/*', '/services/*'] } },
      { not: { selector_matches: '[target=_blank], [rel~=nofollow], [download], [href*="?"]' } },
    ] }, eagerness: 'moderate' }] });
    document.head.append(s);
  }
  const kzWarmed = new Set();
  const kzWarmHero = (e) => {
    const card = e.target.closest?.('a.kz-ecard, a.kz-trow, a.kz-row');
    const img = card?.querySelector('img');
    if (!img || !img.currentSrc && !img.src) return;
    try {
      const u = new URL(img.currentSrc || img.src, location.href);
      const need = (innerWidth >= 990 ? 375 : innerWidth) * (devicePixelRatio || 1);
      u.searchParams.set('width', String([375, 560, 750, 900].find((w) => w >= need) || 900));
      u.searchParams.delete('height'); u.searchParams.delete('crop');
      if (kzWarmed.has(u.href)) return;
      kzWarmed.add(u.href);
      new Image().src = u.href;
    } catch { /* ignore */ }
  };
  document.addEventListener('pointerover', kzWarmHero, { passive: true });
  document.addEventListener('touchstart', kzWarmHero, { passive: true });
  // Back from the checkout (back/forward cache): give the buttons back.
  addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('.kz-checking').forEach((btn) => { kzCheckReset(btn); if (btn.matches('[data-kz-card-buy], [data-kz-sticky-buy]')) btn.disabled = false; });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
